import { Component, input, output, signal, computed, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, catchError, of } from 'rxjs';
import { UserManagementService, OrganizationUser } from 'user-management-lib';
import {
  AuthorSelection,
  AuthorChangeEvent,
  AuthorFilterConfig,
  DEFAULT_AUTHOR_FILTER_CONFIG
} from './author-filter.model';

/**
 * Reusable Author Filter Component
 * 
 * @remarks
 * A standalone component for selecting authors (users with Admin or Editor roles).
 * Features include searchable dropdown, loading states, error handling, and accessibility.
 * 
 * **Features:**
 * - Loads authors from UserManagementService
 * - Searchable dropdown (filters by name/email)
 * - Loading states with spinner
 * - Error handling with clear messaging
 * - Empty state messaging
 * - Clear selection functionality
 * - Light/dark mode support
 * - Keyboard accessible (WCAG 2.1 AA)
 * - Auto-load or manual load options
 * 
 * @example
 * ```html
 * <cms-author-filter
 *   [organizationId]="orgId()"
 *   [selectedAuthorId]="authorId()"
 *   [label]="'Filter by Author'"
 *   [showSearch]="true"
 *   (authorChange)="onAuthorSelected($event)"
 *   (clear)="clearAuthorFilter()"
 * />
 * ```
 * 
 * @example
 * ```typescript
 * // In parent component
 * onAuthorSelected(author: AuthorSelection | null): void {
 *   this.selectedAuthorId.set(author?.id || null);
 *   this.triggerSearch();
 * }
 * ```
 */
@Component({
  selector: 'cms-author-filter',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './author-filter.component.html',
  styleUrls: ['./author-filter.component.css']
})
export class AuthorFilterComponent implements OnInit, OnDestroy {
  // ============================================================================
  // DEPENDENCY INJECTION
  // ============================================================================

  private userManagementService = inject(UserManagementService);
  private destroy$ = new Subject<void>();

  // ============================================================================
  // INPUTS
  // ============================================================================

  /** Organization ID to load authors from */
  organizationId = input.required<string>();

  /** Currently selected author ID */
  selectedAuthorId = input<string | null>(null);

  /** Label displayed above the filter */
  label = input<string>(DEFAULT_AUTHOR_FILTER_CONFIG.label);

  /** Placeholder text for the dropdown */
  placeholder = input<string>(DEFAULT_AUTHOR_FILTER_CONFIG.placeholder);

  /** Placeholder text for the search input */
  searchPlaceholder = input<string>(DEFAULT_AUTHOR_FILTER_CONFIG.searchPlaceholder);

  /** Whether to show the search input */
  showSearch = input<boolean>(DEFAULT_AUTHOR_FILTER_CONFIG.showSearch);

  /** Minimum number of authors before showing search */
  searchThreshold = input<number>(DEFAULT_AUTHOR_FILTER_CONFIG.searchThreshold);

  /** Whether to show the clear button */
  showClearButton = input<boolean>(DEFAULT_AUTHOR_FILTER_CONFIG.showClearButton);

  /** Whether the filter is disabled */
  disabled = input<boolean>(DEFAULT_AUTHOR_FILTER_CONFIG.disabled);

  /** Maximum number of authors to load */
  maxAuthors = input<number>(DEFAULT_AUTHOR_FILTER_CONFIG.maxAuthors);

  /** Whether to auto-load authors on init */
  autoLoad = input<boolean>(DEFAULT_AUTHOR_FILTER_CONFIG.autoLoad);

  /** Custom empty state message */
  emptyMessage = input<string>(DEFAULT_AUTHOR_FILTER_CONFIG.emptyMessage);

  /** Custom error message */
  errorMessage = input<string>(DEFAULT_AUTHOR_FILTER_CONFIG.errorMessage);

  /** Custom loading message */
  loadingMessage = input<string>(DEFAULT_AUTHOR_FILTER_CONFIG.loadingMessage);

  // ============================================================================
  // OUTPUTS
  // ============================================================================

  /** Emitted when an author is selected or cleared */
  authorChange = output<AuthorChangeEvent>();

  /** Emitted when the clear button is clicked */
  clear = output<void>();

  /** Emitted when author loading fails */
  loadError = output<string>();

  // ============================================================================
  // INTERNAL STATE
  // ============================================================================

  /** List of available authors */
  protected authors = signal<AuthorSelection[]>([]);

  /** Whether authors are being loaded */
  protected loading = signal<boolean>(false);

  /** Error message if loading failed */
  protected error = signal<string | null>(null);

  /** Search term for filtering authors */
  protected searchTerm = signal<string>('');

  /** Filtered authors based on search term */
  protected filteredAuthors = computed(() => {
    const authors = this.authors();
    const term = this.searchTerm().toLowerCase().trim();

    if (!term) {
      return authors;
    }

    return authors.filter(author =>
      author.name.toLowerCase().includes(term) ||
      author.email.toLowerCase().includes(term)
    );
  });

  /** Whether to show the search input (based on threshold) */
  protected shouldShowSearch = computed(() => {
    return this.showSearch() && this.authors().length > this.searchThreshold();
  });

  /** Selected author object */
  protected selectedAuthor = computed(() => {
    const authorId = this.selectedAuthorId();
    if (!authorId) return null;
    return this.authors().find(a => a.id === authorId) || null;
  });

  /** Whether any author is selected */
  protected hasSelection = computed(() => !!this.selectedAuthorId());

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    if (this.autoLoad()) {
      this.loadAuthors();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  /**
   * Manually trigger author loading
   * Useful when autoLoad is false
   */
  public loadAuthors(): void {
    const orgId = this.organizationId();

    if (!orgId) {
      console.warn('[AuthorFilter] Cannot load authors: Organization ID not provided');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.userManagementService.getOrganizationUsers(orgId, { 
      limit: this.maxAuthors() 
    })
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('[AuthorFilter] Failed to load authors:', error);
          const errorMsg = this.errorMessage();
          this.error.set(errorMsg);
          this.loading.set(false);
          this.loadError.emit(errorMsg);
          return of({ 
            data: { 
              users: [], 
              pagination: { total: 0, page: 1, limit: this.maxAuthors(), totalPages: 0, hasMore: false }, 
              organization: { id: '', name: '', orgCode: '' } 
            }, 
            status: 'error' as const 
          });
        })
      )
      .subscribe(response => {
        // Filter to only Admin and Editor roles (users who can create/edit posts)
        const authorUsers = response.data.users.filter(user =>
          user.organizationRole === 'Admin' || user.organizationRole === 'Editor'
        );

        // Map to AuthorSelection format
        const authors: AuthorSelection[] = authorUsers.map(user => ({
          id: user._id,
          name: user.name,
          email: user.email,
          role: user.organizationRole
        }));

        this.authors.set(authors);
        this.loading.set(false);

        console.log(`[AuthorFilter] Loaded ${authors.length} authors (Admin + Editor)`);
      });
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle author selection change from dropdown
   * 
   * @param authorId - The selected author ID (empty string for "All authors")
   */
  protected onAuthorSelect(authorId: string): void {
    if (this.disabled()) return;

    const selectedId = authorId || null;
    const author = selectedId ? this.authors().find(a => a.id === selectedId) || null : null;

    this.authorChange.emit(author);
  }

  /**
   * Handle search term change
   * 
   * @param term - The search term
   */
  protected onSearchChange(term: string): void {
    this.searchTerm.set(term);
  }

  /**
   * Clear the author selection
   */
  protected clearSelection(): void {
    if (this.disabled()) return;

    this.searchTerm.set('');
    this.authorChange.emit(null);
    this.clear.emit();
  }

  /**
   * Retry loading authors after an error
   */
  protected retryLoad(): void {
    this.loadAuthors();
  }
}
